import { type CSSProperties } from 'react';
import { Link } from 'react-router';
import { Menu, MenuItem } from '../ui/Menu';
import { itemCountLabel } from '../ui/counts';
import type { SortBinding } from '../ui/useDragSort';
import { sectionName } from '../db/sections';
import type { Section } from '../db/schema';
import type { Placement } from './masonry';
import { SectionCover } from './SectionCover';

/**
 * A section on the board, sized and placed by the same masonry as the items
 * around it. It has to sit in that grid as an equal — the whole gesture that
 * makes one starts with an item being dropped on another.
 *
 * The count underneath is every item inside at any depth. Sections don't
 * count themselves; only the things you actually bought.
 */
export function SectionTile({
  section,
  count,
  covers,
  placement,
  roomId,
  animate,
  lifted,
  merging,
  sort,
  onEdit,
  onDelete,
}: {
  section: Section;
  count: number;
  covers: readonly string[];
  placement: Placement;
  roomId: string;
  animate: boolean;
  lifted: boolean;
  /** Something is being held over it: it's about to swallow whatever that is. */
  merging: boolean;
  sort: SortBinding;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const name = sectionName(section);

  const classes = [
    'tile',
    'tile--section',
    animate ? 'tile--animate' : '',
    lifted ? 'lifted' : '',
    merging ? 'tile--merging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      className={classes}
      style={
        {
          '--tile-x': `${placement.x}px`,
          '--tile-y': `${placement.y}px`,
          width: `${placement.w}px`,
        } as CSSProperties
      }
      {...sort}
    >
      <Link to={`/room/${roomId}/s/${section.id}`} className="tile__link" draggable={false}>
        <div className="tile__frame" style={{ height: `${placement.h}px` }}>
          <SectionCover coverImageId={section.coverImageId} covers={covers} />
        </div>

        <div className="tile__caption tile__caption--priced">
          <p className="tile__title">{name}</p>
          <p className="tile__price">{itemCountLabel(count)}</p>
        </div>
      </Link>

      <div className="tile__actions">
        <Menu label={`Actions for ${name}`}>
          {(close) => (
            <>
              <MenuItem
                onSelect={() => {
                  close();
                  onEdit();
                }}
              >
                Edit Section
              </MenuItem>
              <MenuItem
                onSelect={() => {
                  close();
                  onDelete();
                }}
              >
                Delete Section
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    </article>
  );
}
